import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { employeeService } from '../services/employeeService';
import { EmployeeRecord, EmployeeStatus, Position } from '../types';
import toast from 'react-hot-toast';
import { FiEye, FiCheck, FiX, FiEdit, FiUserPlus, FiTrash2, FiDownload, FiSearch, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { LOCATIONS_OPTIONS } from '../types';
import { getPositions } from '../services/positionsService';
import { TableSkeleton, LoadingPill } from '../components/Skeleton';
import { useMinDelayed } from '../hooks/useMinDelayed';

// Map status to display names and descriptions
const STATUS_CONFIG: Record<string, { 
  title: string; 
  description: string; 
  statusFilter: EmployeeStatus[];
  allowedRoles: string[];
  actions: string[];
}> = {
  new: {
    title: 'New Submissions',
    description: 'Recent applications awaiting initial review',
    statusFilter: ['new_submission'],
    allowedRoles: ['hr_clerk', 'admin', 'viewer'],
    actions: ['view', 'approve', 'reject', 'request_rework']
  },
  'under-review': {
    title: 'Under Review',
    description: 'Applications currently being reviewed by HR',
    statusFilter: ['under_review'],
    allowedRoles: ['hr_clerk', 'admin', 'viewer'],
    actions: ['view', 'approve', 'reject', 'request_rework']
  },
  'rework-requested': {
    title: 'Rework Requested',
    description: 'Applications requiring additional information or corrections from candidates',
    statusFilter: ['requested_rework'],
    allowedRoles: ['hr_clerk', 'admin', 'viewer'],
    actions: ['view', 'approve', 'reject', 'request_rework']
  },
  verified: {
    title: 'Profiles Verified',
    description: 'Applications that have been verified and are ready for hiring consideration',
    statusFilter: ['profile_verified'],
    allowedRoles: ['hr_clerk', 'admin', 'viewer'],
    actions: ['view', 'add_available', 'reject']
  },
  available: {
    title: 'Available for Hire',
    description: 'Candidates ready to be hired',
    statusFilter: ['available_for_hire'],
    allowedRoles: ['hr_clerk', 'admin', 'manager', 'viewer'],
    actions: ['view', 'hire', 'reject']
  },
  hired: {
    title: 'Hired Employees',
    description: 'Recently hired employees requiring onboarding setup',
    statusFilter: ['hired', 'current_employee'],
    allowedRoles: ['hr_clerk', 'admin', 'viewer'],
    actions: ['view', 'update_details', 'terminate', 'blacklist']
  },
  rejected: {
    title: 'Rejected Applications',
    description: 'Applications that were not accepted',
    statusFilter: ['rejected'],
    allowedRoles: ['hr_clerk', 'admin', 'viewer'],
    actions: ['view', 'relist_review', 'blacklist']
  },
  terminated: {
    title: 'Terminated Employees',
    description: 'Employees who have been terminated',
    statusFilter: ['terminated'],
    allowedRoles: ['hr_clerk', 'admin', 'viewer'],
    actions: ['view', 'relist_review', 'blacklist']
  },
  blacklisted: {
    title: 'Blacklisted Candidates',
    description: 'Candidates who are not eligible for future applications',
    statusFilter: ['blacklisted'],
    allowedRoles: ['hr_clerk', 'admin', 'viewer'],
    actions: ['view', 'relist_review']
  }
};

const SubmissionsPage: React.FC = () => {
  const { status = 'new' } = useParams<{ status: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<EmployeeRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [positions, setPositions] = useState<Position[]>([]);

  // Temp filter states (for input fields)
  const [tempGlobalFilter, setTempGlobalFilter] = useState('');
  const [tempPositionFilter, setTempPositionFilter] = useState('');
  const [tempLocationFilter, setTempLocationFilter] = useState('');

  // Actual filter states (for API calls)
  const [globalFilter, setGlobalFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const config = STATUS_CONFIG[status];

  const handleSearch = () => {
    setPage(1); // Reset to first page when searching
    setGlobalFilter(tempGlobalFilter);
    setPositionFilter(tempPositionFilter);
    setLocationFilter(tempLocationFilter);
  };

  const handleClearFilters = () => {
    setTempGlobalFilter('');
    setTempPositionFilter('');
    setTempLocationFilter('');
    setGlobalFilter('');
    setPositionFilter('');
    setLocationFilter('');
    setPage(1);
  };

  useEffect(() => {
    if (!config) {
      navigate('/dashboard');
      return;
    }

    // Check if user has permission to view this page
    if (!user || !config.allowedRoles.includes(user.role)) {
      navigate('/dashboard');
      toast.error('You do not have permission to view this page');
      return;
    }

    loadSubmissions();
  }, [status, user, config, navigate, page, globalFilter, positionFilter, locationFilter]);

  useEffect(() => {
    const loadPositions = async () => {
      try {
        const positionsData = await getPositions();
        setPositions(positionsData);
      } catch (error) {
        console.error('Failed to load positions:', error);
      }
    };
    loadPositions();
  }, []);

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const filters: Record<string, any> = { status: config.statusFilter };
      if (globalFilter) filters.searchTerm = globalFilter;
      if (positionFilter) filters.position = positionFilter;
      if (locationFilter) filters.location = locationFilter;

      const response = await employeeService.getEmployees({
        page: 1,
        limit: 10000,
        filters: filters
      });

      const allData = response.data?.data || [];

      if (allData.length === 0) {
        toast.error('No data to export');
        return;
      }

      const csvHeaders = [
        'Full Names',
        'Also Known As',
        'Surname',
        'Maiden Name',
        'Date of Birth',
        'Gender',
        'Nationality',
        'ID Number',
        'Contact Number',
        'Email',
        'Tax Number',
        'Social Security Number',
        'Street Address',
        'Town/City',
        'Custom Town/City',
        'Bank',
        'Branch',
        'Account Number',
        'Account Type',
        'Account Name',
        'Marital Status',
        'Criminal Records',
        'Pre-existing Disability',
        'Disability Description',
        'Qualification',
        'Employment Status',
        'Current Employer',
        'Current Position',
        'Position Applied For',
        'Previously Employed by Trigon',
        'Previous Trigon Position',
        'Previous Trigon Employee Number',
        'Employee Number',
        'Job Grade',
        'Job Title',
        'Department',
        'Status',
        'Version',
        'Created At',
        'Updated At',
        'Created By',
        'Updated By',
        'Review Notes'
      ];

      const csvRows = allData.map((employee: EmployeeRecord) => [
        employee.fullNames || '',
        employee.alsoKnownAs || '',
        employee.surname || '',
        employee.maidenName || '',
        employee.dateOfBirth || '',
        employee.gender || '',
        employee.nationality || '',
        employee.idNumber || '',
        employee.contactNumber || '',
        employee.email || '',
        employee.taxNumber || '',
        employee.socialSecurityNumber || '',
        employee.residentialAddress?.streetAddress || '',
        employee.residentialAddress?.townCity || '',
        employee.residentialAddress?.customTownCity || '',
        employee.bankingDetails?.bank || '',
        employee.bankingDetails?.branch || '',
        employee.bankingDetails?.accountNumber || '',
        employee.bankingDetails?.accountType || '',
        employee.bankingDetails?.accountName || '',
        employee.maritalStatus || '',
        employee.criminalRecords || '',
        employee.preExistingDisability || '',
        employee.disabilityDescription || '',
        employee.qualification || '',
        employee.employmentStatus || '',
        employee.currentEmployer || '',
        employee.currentPosition || '',
        Array.isArray(employee.positionAppliedFor) ? employee.positionAppliedFor.join(', ') : employee.positionAppliedFor || '',
        employee.previouslyEmployedByTrigon || '',
        employee.previousTrigonPosition || '',
        employee.previousTrigonEmployeeNumber || '',
        employee.employeeNumber || '',
        employee.jobGrade || '',
        employee.jobTitle || '',
        employee.department || '',
        employee.status || '',
        employee.version || '',
        new Date(employee.createdAt).toLocaleDateString(),
        new Date(employee.updatedAt).toLocaleDateString(),
        employee.createdBy || '',
        employee.updatedBy || '',
        employee.reviewNotes || ''
      ]);

      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `${status}_submissions_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const filters: Record<string, any> = { status: config.statusFilter };

      if (globalFilter) filters.searchTerm = globalFilter;
      if (positionFilter) filters.position = positionFilter;
      if (locationFilter) filters.location = locationFilter;

      // Backend now uses Query with GSI - gets ALL matching records efficiently
      const response = await employeeService.getEmployees({
        page,
        limit,
        filters: filters
      });

      if (response.success && response.data) {
        setSubmissions(response.data.data);
        setTotalRecords(response.data.pagination?.total || 0);
      } else {
        toast.error(response.error || 'Failed to load submissions');
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };





  const getStatusBadge = (status: EmployeeStatus) => {
    const statusMap: Record<EmployeeStatus, { color: string; label: string }> = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      new_submission: { color: 'bg-blue-100 text-blue-800', label: 'New' },
      candidate: { color: 'bg-yellow-100 text-yellow-800', label: 'Candidate' },
      under_review: { color: 'bg-yellow-100 text-yellow-800', label: 'Under Review' },
      requested_rework: { color: 'bg-orange-100 text-orange-800', label: 'Rework Requested' },
      profile_verified: { color: 'bg-green-100 text-green-800', label: 'Verified' },
      available_for_hire: { color: 'bg-purple-100 text-purple-800', label: 'Available' },
      hired: { color: 'bg-green-100 text-green-800', label: 'Hired' },
      current_employee: { color: 'bg-green-100 text-green-800', label: 'Current Employee' },
      past_employee: { color: 'bg-gray-100 text-gray-800', label: 'Past Employee' },
      terminated: { color: 'bg-red-100 text-red-800', label: 'Terminated' },
      blacklisted: { color: 'bg-black text-white', label: 'Blacklisted' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
    };

    const { color, label } = statusMap[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  const renderViewButton = (submission: EmployeeRecord) => {
    // For rework requested status, show candidate view instead of HR review
    const viewUrl = submission.status === 'requested_rework' 
      ? `/candidate/${submission.id}` 
      : `/review/${submission.id}`;
    
    const titleText = submission.status === 'requested_rework'
      ? "View Candidate's Application (what they see)"
      : "View Details";
    
    return (
      <div className="flex space-x-2">
        <Link
          to={viewUrl}
          className="text-blue-600 hover:text-blue-900"
          title={titleText}
        >
          <FiEye className="h-4 w-4" />
        </Link>
      </div>
    );
  };

  const showSkeleton = useMinDelayed(loading, 500);

  if (!config) {
    return <div>Invalid status</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{config.title}</h1>
          <p className="text-gray-600 mt-1">{config.description}</p>
          <div className="mt-2 flex items-center gap-3">
            {showSkeleton ? (
              <LoadingPill label={`Loading ${config.title.toLowerCase()}`} />
            ) : (
              <span className="text-sm text-gray-500">
                {submissions.length} submission(s) found
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          {/* New button - only show in staging on /submissions/new */}
          {status === 'new' && (
            <Link
              to="/register"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiUserPlus className="mr-2" />
              New
            </Link>
          )}          
          <button
            onClick={exportToCSV}
            disabled={isExporting}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiDownload className="mr-2" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={tempGlobalFilter}
                onChange={(e) => setTempGlobalFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search submissions..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Position Filter</label>
            <select
              value={tempPositionFilter}
              onChange={(e) => setTempPositionFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 w-full"
            >
              <option value="">All Positions</option>
              {positions.filter(pos => pos.isActive).map(position => (
                <option key={position.id} value={position.name}>{position.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location Filter</label>
            <select
              value={tempLocationFilter}
              onChange={(e) => setTempLocationFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 w-full"
            >
              <option value="">All Locations</option>
              {LOCATIONS_OPTIONS.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {showSkeleton ? (
        <TableSkeleton rows={limit} cols={5} />
      ) : (
      <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Candidate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Position
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Submitted
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                View
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {submissions.map((submission) => (
              <tr key={submission.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {submission.fullNames} {submission.surname}
                    </div>
                    <div className="text-sm text-gray-500 break-all">{submission.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {Array.isArray(submission.positionAppliedFor) ? submission.positionAppliedFor.join(', ') : submission.positionAppliedFor}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(submission.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(submission.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {renderViewButton(submission)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {submissions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No submissions found for this status.
          </div>
        )}

        {/* Pagination */}
        {totalRecords > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <span className="text-sm text-gray-700">
              Page <strong>{page}</strong> of <strong>{Math.ceil(totalRecords / limit)}</strong> | Total Records: {totalRecords}
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                <FiChevronsLeft />
              </button>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                <FiChevronLeft />
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(totalRecords / limit)}
                className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                <FiChevronRight />
              </button>
              <button
                onClick={() => setPage(Math.ceil(totalRecords / limit))}
                disabled={page >= Math.ceil(totalRecords / limit)}
                className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                <FiChevronsRight />
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default SubmissionsPage;
